import { memo, useCallback, useMemo, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interaction from '@fullcalendar/interaction';
import styled, { css } from 'styled-components';
import dayjs from 'dayjs';

import Row from 'components/common/Row';
import Text from 'components/common/Text';
import { Option } from 'types/common.types';

import { FixedTicket, ScheduleState, UnfixedTicket } from 'types/api';
import { EventSourceInput } from '@fullcalendar/core';
import ProductStatusBadge from './ProductStatusBadge';

const CalendarContainer = styled.div<{
  $isMobile: boolean;
  $isWebkit: boolean;
}>`
  width: 100%;
  margin-bottom: 28px;

  .fc {
    --fc-border-size: 0.5px;
    --fc-border-color: #dbdcdf;
    font-weight: ${props => (props.$isMobile ? '600' : '500')};

    .fc-daygrid-day.fc-day-other {
      background-color: #ffffff !important;
    }

    .fc-daygrid-day.fc-day-other .fc-daygrid-day-number {
      visibility: hidden; // 날짜 숨기기
    }

    .fc-daygrid-day.fc-day-other .fc-daygrid-day-events {
      display: none;
    }

    .fc-scrollgrid {
      border: none;
    }

    thead {
      display: none;
    }

    td {
      border: none;
      border-left: 0.5px solid #dbdcdf;
      border-bottom: 0.5px solid #dbdcdf;

      &:first-child {
        border: none;
        border-bottom: 0.5px solid #dbdcdf;
      }
    }

    .fc-daygrid-day-frame {
      display: flex;
      flex-direction: column;

      min-height: 110px;

      /* padding: 10px 8px 64px; */
      padding: 10px 0px 64px;

      > .fc-daygrid-day-top {
        margin-bottom: 8px;
      }
    }

    .fc-daygrid-day-events {
      display: flex;
      flex-direction: column;

      min-height: 0px;
      margin-bottom: 0px;
    }

    .fc-event {
      background: #f7f7f8;

      border: none;
      margin: 0;
      border-radius: 12px;
      margin-bottom: 8px;

      &.fc-event-end {
        > .fc-event-main {
          div[data-type='tag'] {
            display: none;
          }
        }
      }

      &.fc-event-start {
        &.fc-event-end {
          border-radius: 12px;
          margin-left: 8px;
          margin-right: 8px;
        }

        &:not(.fc-event-end) {
          border-radius: 12px 0 0 12px;
          margin-left: 8px;
        }
      }

      &.fc-event-end {
        &:not(.fc-event-start) {
          border-radius: 0px 12px 12px 0px;
          margin-right: 8px;
        }
      }

      /* 이하 넘어갈때 종료일 이벤트 표시 안되게 처리 */
      /* &.fc-event-end {
        > .fc-event-main {
          opacity: 0;
        }

        &.fc-event-start {
          > .fc-event-main {
            opacity: 1;
          }
        }
      } */
    }

    .fc-highlight {
      background: none;
    }

    .fc-col-header {
      display: none;
    }

    .fc-daygrid-day-top {
      display: flex;
      justify-content: flex-end;
      width: 100%;

      padding: 0px 8px;
    }

    .fc-daygrid-day-number {
      font-size: 14px;
      text-align: left;
    }

    .fc-daygrid-day.fc-day-disabled .fc-daygrid-day-number {
      color: #c2c4c8;
    }

    .fc-daygrid-day-number {
      color: #70737c;
    }

    .fc-daygrid-day.fc-day-disabled {
      background-color: #f9fafb;
      color: #c2c4c8;
      text-decoration: line-through;
    }

    .fc-daygrid-day.fc-day-selected {
      .fc-daygrid-day-top {
        width: 100%;
      }

      .fc-daygrid-day-number {
        width: 100%;
      }

      .selected-date {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 4px;
        width: 100%;
      }

      .selected-date-info {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 2px;
      }

      .fc-daygrid-day-number {
        font-size: 14px;
        text-align: left;
      }

      color: #70737c;
      font-weight: 600;
      background-color: #eaebec;
    }

    .fc-daygrid-day.fc-day-today {
      background-color: #fff;
    }

    .fc-daygrid-day.fc-day-today {
      .fc-daygrid-day-top {
        a {
          color: #0071e9;

          &::after {
            content: '오늘';
            margin-left: 4px;

            font-weight: 400;
            font-size: 14px;
            line-height: 20px;
            letter-spacing: -0.4%;
          }
        }
      }
    }

    .fc-header-toolbar {
      display: none;
    }
    ${props =>
      props.$isWebkit &&
      css`
        td {
          border: none;
          border-left: 0.7px solid #dbdcdf;
          border-bottom: 0.7px solid #dbdcdf;

          &:first-child {
            border: none;
            border-bottom: 0.7px solid #dbdcdf;
          }
        }
      `}

    .schedule-card-box {
      border: 1px solid transparent;
    }

    // 비활성화된 날짜는 커서 변경 없음
    .fc-daygrid-day.fc-day-disabled {
      cursor: default;
    }
  }
`;

const ScheduleCardBox = styled.div<{ $width: string }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 6px;

  padding: 8px 0;
  margin: 0;
  width: 100%;

  border-radius: 12px;
  background: #f7f7f8;

  white-space: wrap;
  word-break: keep-all;

  cursor: pointer;

  > div[data-type='title-box'] {
    display: flex;
    justify-content: space-between;
    gap: 4px;

    width: 100%;
    min-height: 32px;
    max-width: ${props => props.$width ?? '100%'};

    padding: 0 8px;

    > img {
      max-width: 24px;
      max-height: 24px;
      min-width: 24px;
      min-height: 24px;

      border-radius: 6px;
    }

    > p {
      width: 100%;

      font-weight: 400;
      font-size: 12px;
      line-height: 16px;
      letter-spacing: -0.4px;
      color: #70737c;
      word-break: break-all;

      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      width: 100%;

      &.canceled {
        color: #c2c4c8;
      }
    }
  }

  > div[data-type='date-box'] {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;

    padding: 0 8px;

    width: 100%;

    white-space: nowrap;
    overflow-x: scroll;

    padding: 0 8px;

    &::-webkit-scrollbar {
      display: none;
    }

    > p {
      color: #1b1c1e;
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
      letter-spacing: -0.056px;

      overflow-x: scroll;
      white-space: nowrap;

      &.canceled {
        color: #c2c4c8;
      }
    }
  }
`;

const TagBox = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 4px;

  width: 100%;

  white-space: nowrap;
  overflow-x: scroll;

  padding: 0 8px;

  &::-webkit-scrollbar {
    display: none;
  }

  > div[data-type='tag'] {
    padding: 0px 6px;
    border-radius: 6px;
    background: #dce2e9;

    font-weight: 600;
    font-size: 11px;
    line-height: 24px;
    letter-spacing: -0.2px;
    text-align: center;
    color: #414b56;
  }
`;

export const WEEK_DAYS = [
  {
    name: '일',
    color: '#FF4242',
  },
  {
    name: '월',
    color: '#70737c',
  },
  {
    name: '화',
    color: '#70737c',
  },
  {
    name: '수',
    color: '#70737c',
  },
  {
    name: '목',
    color: '#70737c',
  },
  {
    name: '금',
    color: '#70737c',
  },
  {
    name: '토',
    color: '#06F',
  },
];

interface EventCardProps {
  eventInfo: any;
  startDate: Date;
  dayCellWidth: number | undefined;
  selectItemHandler: (item: UnfixedTicket | FixedTicket) => void;
}

const EventCard = memo(
  ({ eventInfo, startDate, dayCellWidth, selectItemHandler }: EventCardProps) => {
    const item = eventInfo.event.extendedProps.item;
    const isUnfixedTicket = 'bookingItem' in item;

    const { ticketId, endDateTimeStamp, startDateTimeStamp } = useMemo(() => {
      if (isUnfixedTicket) {
        const unFixedItem = item as UnfixedTicket;
        return {
          ticketId: unFixedItem.id,
          endDateTimeStamp: unFixedItem.scheduleTerm?.endedAt || 0,
          startDateTimeStamp: unFixedItem.scheduleTerm?.startedAt || 0,
        };
      }
      const fixedItem = item as FixedTicket;
      return {
        ticketId: fixedItem.id,
        endDateTimeStamp: fixedItem.term?.endedAt || 0,
        startDateTimeStamp: fixedItem.term?.startedAt || 0,
      };
    }, [item, isUnfixedTicket]);

    const { endDay, startDay, isExactlySameDay, isSameDay, dayDiff } = useMemo(() => {
      const end = dayjs(new Date(endDateTimeStamp));
      const start = dayjs(new Date(startDateTimeStamp));
      return {
        endDay: end,
        startDay: start,
        isExactlySameDay: endDateTimeStamp === startDateTimeStamp,
        isSameDay: start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD'),
        dayDiff: end.diff(start, 'day'),
      };
    }, [endDateTimeStamp, startDateTimeStamp]);

    const isScheduleCanceled = item.status === ScheduleState.CANCELED;

    const handleEventCardClick = useCallback(() => {
      selectItemHandler(item);
    }, [selectItemHandler, item]);

    const cardWidth = !isSameDay && dayCellWidth
      ? `${(dayCellWidth - 16).toFixed(1)}px`
      : '100%';

    return (
      <ScheduleCardBox
        className={`schedule-card-box id-${ticketId}`}
        onClick={handleEventCardClick}
        $width={cardWidth}
      >
        <div data-type="title-box">
          <p className={isScheduleCanceled ? 'canceled' : ''}>
            {eventInfo.event.title}
          </p>
          <img src={eventInfo.event.extendedProps.thumbnail} alt="thumbnail" />
        </div>

        <div data-type="date-box">
          <p className={isScheduleCanceled ? 'canceled' : ''}>
            {dayjs(startDate).format('H:mm')} ~{' '}
            {isExactlySameDay
              ? ''
              : isSameDay
              ? endDay.format('H:mm')
              : `${endDay.format(
                  `${dayDiff > 4 ? 'M월 D일(ddd)' : 'D일(ddd)'}`,
                )} ${endDay.format('H:mm')}`}
          </p>
        </div>

        <TagBox>
          <ProductStatusBadge item={item} />
          {!isSameDay && dayDiff + 1 > 1 && (
            <div data-type="tag">{dayDiff + 1}일간</div>
          )}
        </TagBox>
      </ScheduleCardBox>
    );
  },
);

interface Props {
  currentMonth: Option;
  bookingTickets: {
    date: string;
    value: (UnfixedTicket | FixedTicket)[];
  }[];
  selectItemHandler: (item: UnfixedTicket | FixedTicket) => void;
}

function BookingCalendarV2({
  currentMonth,
  bookingTickets,
  selectItemHandler,
}: Props) {
  const dayCellWidthRef = useRef<number | undefined>(undefined);

  const calculateEndDate = useCallback(
    (startDate: number, endDate: number) => {
      const startDay = dayjs(new Date(startDate));
      const endDay = dayjs(new Date(endDate));

      // 달이 다른 경우 시작일 반환
      if (startDay.month() !== endDay.month()) {
        return startDate;
      }

      // 4일 이상 차이나는 경우 시작일 반환
      const diffDays = endDay.diff(startDay, 'day');
      if (diffDays > 4) {
        return startDate;
      }

      // 그 외의 경우, 끝나는 날짜 반환
      return endDate;
    },
    [],
  );

  const eventList: EventSourceInput = useMemo(() => {
    return bookingTickets.flatMap(item => {
      return item.value.map(ticket => {
        const isUnfixedTicket = 'bookingItem' in ticket;

        if (isUnfixedTicket) {
          const unFixedTicket = ticket as UnfixedTicket;
          const startDate = unFixedTicket.scheduleTerm.startedAt;
          const endDate = unFixedTicket.scheduleTerm.endedAt;

          return {
            title: unFixedTicket.bookingItem.booking.info.product.title,
            start: startDate,
            end: calculateEndDate(startDate, endDate),
            extendedProps: {
              item: unFixedTicket,
              thumbnail:
                unFixedTicket.bookingItem.booking.info.product.content
                  .thumbnail,
            },
          };
        }

        const fixedTicket = ticket as FixedTicket;
        const startDate = fixedTicket.term.startedAt;
        const endDate = fixedTicket.term.endedAt;

        return {
          title: fixedTicket.product.title,
          start: startDate,
          end: calculateEndDate(startDate, endDate),
          extendedProps: {
            item: fixedTicket,
            thumbnail:
              fixedTicket.product?.headerContents[0]?.content?.thumbnail || '',
          },
        };
      });
    });
  }, [bookingTickets, calculateEndDate]);

  const renderEventContent = useCallback(
    (eventInfo: any) => {
      // dayCellWidth 캐싱 (최초 한번만 계산)
      if (dayCellWidthRef.current === undefined) {
        const dayCell = document.querySelector('td.fc-day.fc-daygrid-day');
        dayCellWidthRef.current = dayCell?.getBoundingClientRect().width;
      }

      return (
        <EventCard
          eventInfo={eventInfo}
          startDate={eventInfo.event.start}
          dayCellWidth={dayCellWidthRef.current}
          selectItemHandler={selectItemHandler}
        />
      );
    },
    [selectItemHandler],
  );

  return (
    <>
      <CalendarContainer $isMobile={false} $isWebkit={window.webkit}>
        <Row
          style={{
            width: '100%',
            background: '#F2F4F6',
            borderTop: '0.5px solid #dbdcdf',
            borderBottom: 'none',
          }}
        >
          {WEEK_DAYS.map((item, idx) => {
            return (
              <Row
                style={{
                  padding: '10px 8px',
                  width: '100%',
                  borderRight:
                    WEEK_DAYS.length - 1 !== idx
                      ? window.webkit
                        ? '0.7px solid #dbdcdf'
                        : '0.5px solid #dbdcdf'
                      : 'none',
                }}
                key={item.name}
              >
                <Text color={item.color}>{item.name}</Text>
              </Row>
            );
          })}
        </Row>

        <FullCalendar
          plugins={[dayGridPlugin, interaction]}
          initialView="dayGridMonth"
          initialDate={
            currentMonth?.value
              ? new Date(Number(currentMonth?.value))
              : new Date()
          }
          headerToolbar={false}
          selectable={false}
          selectMirror={true}
          dayMaxEvents={true}
          firstDay={0} // 일요일부터 시작
          height="auto"
          showNonCurrentDates={true}
          fixedWeekCount={false}
          events={eventList}
          eventContent={renderEventContent}
        />
      </CalendarContainer>
    </>
  );
}

export default BookingCalendarV2;
